/**
 * GirlsBandTabiPage
 * Landing page for GirlsBandTabi — 성지순례 앱
 * 5섹션: Hero · Features · Screenshots · HowItWorks · DownloadCTA
 *
 * Design: Anime × premium SaaS landing aesthetic
 * Constraints: CSS keyframes only (no Framer Motion), no images (CSS mockups)
 */
import React, { useEffect, useRef, useState } from 'react';

// ─── Scroll Reveal Hook ───────────────────────────────────────────────────────

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface FeatureCard {
  icon: string;
  num: string;
  title: string;
  desc: string;
  accent: string;
}

interface Step {
  num: string;
  title: string;
  desc: string;
  icon: string;
}

interface Screenshot {
  label: string;
  gradient: string;
  icon: string;
  screenItems: string[];
  /** TODO: Replace with actual screenshot path, e.g. "/screenshots/home.png" */
  src?: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const features: FeatureCard[] = [
  {
    icon: '📍',
    num: '01',
    title: '성지 지도',
    desc: 'GPS 탐색으로 성지를 찾고 최적 루트까지 길찾기',
    accent: '#4A7FB7',
  },
  {
    icon: '📖',
    num: '02',
    title: '순례 도감',
    desc: '방문한 성지마다 스탬프 — 내 순례 기록을 컬렉션으로',
    accent: '#FFC843',
  },
  {
    icon: '🎵',
    num: '03',
    title: '라이브 일정',
    desc: '콘서트·팬미팅·이벤트 일정을 한눈에 관리',
    accent: '#77DD77',
  },
  {
    icon: '💬',
    num: '04',
    title: '커뮤니티',
    desc: '팬들과 여행기를 나누는 실시간 피드',
    accent: '#C084FC',
  },
  {
    icon: '📺',
    num: '05',
    title: '밴드 피드',
    desc: '프로젝트별 최신 소식과 멤버 정보를 한 곳에서',
    accent: '#FB923C',
  },
  {
    icon: '⭐',
    num: '06',
    title: '팬 레벨',
    desc: 'XP 시스템과 랭킹으로 나만의 팬심을 증명',
    accent: '#F472B6',
  },
];

const steps: Step[] = [
  {
    num: '01',
    icon: '🎸',
    title: '밴드 · 작품 선택',
    desc: '좋아하는 밴드나 작품을 선택해 내 도감을 시작합니다.',
  },
  {
    num: '02',
    icon: '🗺️',
    title: '성지 탐색',
    desc: '지도에서 근처 성지와 방문 루트를 실시간으로 확인합니다.',
  },
  {
    num: '03',
    icon: '📸',
    title: '방문 인증 & 기록',
    desc: '현장에서 GPS 인증 후 기록을 남기고 팬들과 공유합니다.',
  },
];

const screenshots: Screenshot[] = [
  {
    label: '홈',
    gradient: 'linear-gradient(160deg, #1a2a4a 0%, #2952A3 100%)',
    icon: '🏠',
    screenItems: ['최근 성지', '인기 루트', '오늘의 이벤트'],
  },
  {
    label: '지도',
    gradient: 'linear-gradient(160deg, #0f3d2a 0%, #1a6b4a 100%)',
    icon: '📍',
    screenItems: ['GPS 위치', '주변 성지 12', '길찾기'],
  },
  {
    label: '도감',
    gradient: 'linear-gradient(160deg, #3d2800 0%, #7a5200 100%)',
    icon: '📖',
    screenItems: ['수집 32/100', '이번 달 +5', '배지 획득'],
  },
  {
    label: '라이브',
    gradient: 'linear-gradient(160deg, #1a0f3d 0%, #3d1a7a 100%)',
    icon: '🎵',
    screenItems: ['이번 주 공연 3', '알림 설정', '티켓 구매'],
  },
  {
    label: '커뮤니티',
    gradient: 'linear-gradient(160deg, #3d0f2a 0%, #7a1a4a 100%)',
    icon: '💬',
    screenItems: ['피드 128개', '팔로워 팁', '실시간 채팅'],
  },
];

// ─── Phone Mockup ─────────────────────────────────────────────────────────────

interface PhoneMockupProps {
  gradient: string;
  icon: string;
  screenItems: string[];
  style?: React.CSSProperties;
  size?: 'sm' | 'md' | 'lg';
  /** TODO: Pass actual app screenshot path (e.g. src="/screenshots/home.png") */
  src?: string;
  alt?: string;
}

function PhoneMockup({ gradient, icon, screenItems, style, size = 'md', src, alt = '' }: PhoneMockupProps) {
  // iPhone 15 Pro Max proportions
  const dims = {
    sm: { w: 138, h: 299, frameR: 22, screenR: 19, bezel: 3, notchW: 50, notchH: 13, btnW: 3 },
    md: { w: 166, h: 360, frameR: 26, screenR: 23, bezel: 3, notchW: 60, notchH: 15, btnW: 3 },
    lg: { w: 194, h: 420, frameR: 30, screenR: 26, bezel: 4, notchW: 70, notchH: 17, btnW: 4 },
  }[size];

  const frame = 'linear-gradient(145deg, #888 0%, #555 28%, #333 50%, #555 72%, #888 100%)';

  return (
    <div
      style={{
        position: 'relative',
        width: dims.w,
        height: dims.h,
        flexShrink: 0,
        ...style,
      }}
    >
      {/* Titanium frame */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: dims.frameR,
          background: frame,
          boxShadow:
            '0 28px 70px rgba(0,0,0,0.55), 0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.4)',
        }}
      />

      {/* Screen area — inset from frame */}
      <div
        style={{
          position: 'absolute',
          inset: dims.bezel,
          borderRadius: dims.screenR,
          overflow: 'hidden',
          background: gradient,
        }}
      >
        {/* Dynamic Island */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 9,
            left: '50%',
            transform: 'translateX(-50%)',
            width: dims.notchW,
            height: dims.notchH,
            borderRadius: 999,
            background: '#000',
            zIndex: 2,
          }}
        />

        {/* Screenshot image */}
        {src && (
          <img
            src={src}
            alt={alt}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top center',
              zIndex: 1,
            }}
          />
        )}

        {/* CSS placeholder */}
        {!src && (
          <div
            style={{
              padding: `${dims.notchH + 22}px 12px 28px`,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              height: '100%',
            }}
          >
            <div
              style={{
                borderRadius: 12,
                background: 'rgba(255,255,255,0.1)',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <span style={{ fontSize: size === 'lg' ? '2.5rem' : '2rem' }}>{icon}</span>
              {screenItems.map((item) => (
                <div
                  key={item}
                  style={{
                    height: 7,
                    borderRadius: 4,
                    background: 'rgba(255,255,255,0.3)',
                    width: `${55 + Math.floor(item.length * 2)}%`,
                  }}
                />
              ))}
            </div>
            <div
              style={{
                height: 34,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                padding: '0 8px',
              }}
            >
              {['⊞', '◎', '♡'].map((sym) => (
                <span key={sym} style={{ fontSize: '0.7rem', opacity: 0.75 }}>
                  {sym}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Home indicator */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            bottom: 7,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '30%',
            height: 4,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.35)',
            zIndex: 3,
          }}
        />
      </div>

      {/* Left buttons — Action, Vol+, Vol- */}
      {[
        { top: '17%', h: dims.btnW === 3 ? 14 : 18 },  // action
        { top: '27%', h: dims.btnW === 3 ? 26 : 32 },  // vol+
        { top: '39%', h: dims.btnW === 3 ? 26 : 32 },  // vol-
      ].map((btn, i) => (
        <div
          key={i}
          aria-hidden
          style={{
            position: 'absolute',
            left: -dims.btnW,
            top: btn.top,
            width: dims.btnW,
            height: btn.h,
            borderRadius: `${dims.btnW}px 0 0 ${dims.btnW}px`,
            background: frame,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15)',
          }}
        />
      ))}

      {/* Right button — Power */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          right: -dims.btnW,
          top: '29%',
          width: dims.btnW,
          height: dims.btnW === 3 ? 38 : 48,
          borderRadius: `0 ${dims.btnW}px ${dims.btnW}px 0`,
          background: frame,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15)',
        }}
      />
    </div>
  );
}

// ─── Store Button ─────────────────────────────────────────────────────────────

function StoreButton({ store, variant = 'light' }: { store: string; variant?: 'light' | 'dark' }) {
  const isDark = variant === 'dark';
  return (
    <button
      disabled
      aria-label={`${store} — 준비중`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem 1.75rem',
        background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--card)',
        border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid var(--stroke)',
        borderRadius: 'var(--radius)',
        color: isDark ? 'rgba(255,255,255,0.75)' : 'var(--muted)',
        fontSize: '0.9rem',
        fontWeight: 600,
        cursor: 'not-allowed',
        letterSpacing: '0.02em',
        backdropFilter: isDark ? 'blur(8px)' : undefined,
        WebkitBackdropFilter: isDark ? 'blur(8px)' : undefined,
      }}
    >
      {store}
      <span
        style={{
          fontSize: '0.65rem',
          fontWeight: 500,
          padding: '0.15rem 0.5rem',
          background: isDark ? 'rgba(255,255,255,0.12)' : 'var(--stroke-subtle)',
          borderRadius: '999px',
          letterSpacing: '0.06em',
          color: isDark ? 'rgba(255,255,255,0.55)' : 'var(--muted)',
        }}
      >
        준비중
      </span>
    </button>
  );
}

// ─── Section 1: Hero ──────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section
      aria-labelledby="gbt-hero-title"
      className="grain-overlay"
      style={{
        minHeight: '100svh',
        padding: 'clamp(4.5rem, 12vw, 7rem) clamp(1.25rem, 5vw, 2.5rem) clamp(3rem, 8vw, 5rem)',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(170deg, #0c0e1a 0%, #111827 55%, #0d1520 100%)',
      }}
    >
      {/* Glow orb — blue, top-left */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '-10%',
          left: '-5%',
          width: '700px',
          height: '700px',
          background:
            'radial-gradient(circle, rgba(74,127,183,0.22) 0%, transparent 65%)',
          animation: 'glow-drift 18s ease-in-out infinite alternate',
          pointerEvents: 'none',
        }}
      />
      {/* Glow orb — yellow, bottom-right */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '5%',
          right: '-5%',
          width: '550px',
          height: '550px',
          background:
            'radial-gradient(circle, rgba(255,200,67,0.16) 0%, transparent 65%)',
          animation: 'glow-drift 22s ease-in-out infinite alternate-reverse',
          pointerEvents: 'none',
        }}
      />
      {/* Glow orb — purple, center */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(192,132,252,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Thin horizontal line accent */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(74,127,183,0.2) 30%, rgba(255,200,67,0.2) 70%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* 2-column layout */}
      <div
        className="hero-inner"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Text column */}
        <div className="hero-text">
          {/* Eyebrow */}
          <div
            className="animate-fade-up stagger-1"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.75rem',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#FFC843',
                boxShadow: '0 0 8px rgba(255,200,67,0.8)',
              }}
            />
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'rgba(255,200,67,0.9)',
              }}
            >
              2026년 상반기 출시
            </span>
          </div>

          {/* Main headline */}
          <h1
            id="gbt-hero-title"
            className="animate-fade-up stagger-2"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(3.5rem, 12vw, 8.5rem)',
              lineHeight: 0.9,
              letterSpacing: '0.015em',
              margin: '0 0 1.5rem',
              color: 'transparent',
              backgroundImage:
                'linear-gradient(135deg, #FFFFFF 0%, #C7D9F0 30%, #4A7FB7 55%, #FFC843 80%, #FFFFFF 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'string-shimmer 6s linear infinite, fade-up 0.7s cubic-bezier(0.22,1,0.36,1) 0.2s both',
            }}
          >
            GIRLSBAND
            <br />
            TABI
          </h1>

          {/* Japanese subtitle */}
          <p
            className="animate-fade-up stagger-3"
            style={{
              fontFamily: 'var(--font-serif-jp)',
              fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
              color: 'rgba(255,255,255,0.55)',
              margin: '0 0 0.4rem',
              letterSpacing: '0.08em',
            }}
          >
            聖地巡礼をもっと、楽しく。
          </p>

          <p
            className="animate-fade-up stagger-3"
            style={{
              fontSize: '1rem',
              color: 'rgba(255,255,255,0.4)',
              marginBottom: '1rem',
              letterSpacing: '0.04em',
            }}
          >
            밴드의 발자국을 따라서
          </p>

          <p
            className="animate-fade-up stagger-4"
            style={{
              fontSize: '0.95rem',
              color: 'rgba(255,255,255,0.5)',
              marginBottom: '2.75rem',
              maxWidth: '400px',
              lineHeight: 1.8,
            }}
          >
            찾고, 저장하고, 함께 이야기하는 걸즈밴드 팬 커뮤니티 앱
          </p>

          {/* Store buttons */}
          <div
            className="animate-fade-up stagger-5 hero-store-btns"
            style={{ display: 'flex', gap: '0.875rem', flexWrap: 'wrap' }}
          >
            <StoreButton store="App Store" variant="dark" />
            <StoreButton store="Google Play" variant="dark" />
          </div>
        </div>

        {/* Phone mockup column */}
        {/* TODO: Replace CSS mockups with actual app screenshots */}
        <div className="hero-phones" aria-hidden>
          {/* Phone 3 — far back, rightmost, feed */}
          <PhoneMockup
            gradient="linear-gradient(160deg, #3d0f2a 0%, #7a1a4a 100%)"
            icon="💬"
            screenItems={['피드', '팔로워', '채팅']}
            size="sm"
            style={{
              position: 'absolute',
              right: -20,
              top: 60,
              transform: 'rotate(10deg)',
              zIndex: 1,
              opacity: 0.55,
            }}
          />
          {/* Phone 2 — behind, left, band feed */}
          <PhoneMockup
            gradient="linear-gradient(160deg, #1a0f3d 0%, #3d1a7a 100%)"
            icon="🎵"
            screenItems={['라이브', '공연', '알림']}
            size="sm"
            style={{
              position: 'absolute',
              left: -20,
              top: 40,
              transform: 'rotate(-8deg)',
              zIndex: 1,
              opacity: 0.55,
            }}
          />
          {/* Phone 1 — front center, map */}
          <PhoneMockup
            gradient="linear-gradient(160deg, #0f3d2a 0%, #1a6b4a 100%)"
            icon="📍"
            screenItems={['GPS 위치', '주변 성지', '길찾기']}
            size="lg"
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              transform: 'translateX(-50%)',
              zIndex: 2,
              animation: 'float-y 6s ease-in-out infinite',
            }}
          />
        </div>
      </div>

      {/* Scroll hint */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.4rem',
          opacity: 0.35,
          animation: 'float-y 3s ease-in-out infinite',
        }}
      >
        <span
          style={{
            fontSize: '0.6rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Scroll
        </span>
        <div
          style={{
            width: 1,
            height: 32,
            background:
              'linear-gradient(to bottom, rgba(255,255,255,0.6), transparent)',
          }}
        />
      </div>

      <style>{`
        .hero-inner {
          display: flex;
          flex-direction: column;
          gap: 2rem;
          align-items: center;
          text-align: center;
          min-height: calc(100svh - clamp(7.5rem, 20vw, 12rem));
          justify-content: center;
        }
        .hero-text { width: 100%; max-width: 600px; }
        .hero-phones {
          display: none;
          position: relative;
          flex-shrink: 0;
        }
        .hero-store-btns { justify-content: center; }
        @media (min-width: 600px) {
          .hero-inner { align-items: flex-start; text-align: left; gap: 2.5rem; }
          .hero-text { max-width: none; }
          .hero-store-btns { justify-content: flex-start; }
        }
        @media (min-width: 860px) {
          .hero-inner {
            flex-direction: row;
            align-items: center;
            text-align: left;
            justify-content: space-between;
            gap: 5rem;
            min-height: calc(100svh - 12rem);
          }
          .hero-text { flex: 1; max-width: none; }
          .hero-phones { display: block; width: 420px; height: 540px; }
        }
      `}</style>
    </section>
  );
}

// ─── Section 2: Features ──────────────────────────────────────────────────────

function FeaturesSection() {
  const { ref: gridRef, visible } = useScrollReveal(0.1);
  return (
    <section
      id="features"
      aria-labelledby="gbt-features-title"
      style={{
        padding: 'clamp(4rem, 10vw, 7rem) clamp(1.25rem, 5vw, 2.5rem)',
        background: 'var(--bg)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid dots */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle, rgba(74,127,183,0.07) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        {/* Section header */}
        <div style={{ marginBottom: 'clamp(2.5rem, 5vw, 4rem)' }}>
          <p
            style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--accent-blue)',
              marginBottom: '0.75rem',
            }}
          >
            What you get
          </p>
          <h2
            id="gbt-features-title"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              letterSpacing: '0.02em',
              margin: 0,
              color: 'transparent',
              backgroundImage:
                'linear-gradient(135deg, var(--text) 40%, var(--accent-blue) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1.0,
            }}
          >
            FEATURES
          </h2>
        </div>

        <div className="features-grid" ref={gridRef}>
          {features.map((f, i) => (
            <div
              key={f.num}
              className={`card-slot${visible ? ' card-slot-visible' : ''}`}
              style={{ '--delay': `${i * 0.09}s` } as React.CSSProperties}
            >
            <article
              className="feature-card"
              style={{
                background: 'var(--card)',
                border: '1px solid var(--stroke)',
                borderRadius: 'var(--radius-lg)',
                padding: '2rem',
                boxShadow: 'var(--shadow-card)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s ease, border-color 0.22s ease',
              }}
            >
              {/* Corner accent gradient */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 100,
                  height: 100,
                  background: `radial-gradient(circle at top right, ${f.accent}18 0%, transparent 70%)`,
                  borderRadius: '0 var(--radius-lg) 0 0',
                  pointerEvents: 'none',
                }}
              />

              {/* Icon pill */}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: `${f.accent}14`,
                  border: `1px solid ${f.accent}28`,
                  fontSize: '1.75rem',
                  marginBottom: '1.25rem',
                }}
              >
                {f.icon}
              </div>

              {/* Number badge */}
              <div
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '1.5rem',
                  fontFamily: 'var(--font-display)',
                  fontSize: '3rem',
                  letterSpacing: '0.02em',
                  color: `${f.accent}55`,
                  lineHeight: 1,
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                aria-hidden
              >
                {f.num}
              </div>

              <h3
                style={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  margin: '0 0 0.6rem',
                  color: 'var(--text)',
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--muted)',
                  margin: 0,
                  lineHeight: 1.75,
                }}
              >
                {f.desc}
              </p>

              {/* Bottom accent line */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, ${f.accent}60, ${f.accent}20)`,
                  borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
                  opacity: 0,
                  transition: 'opacity 0.22s ease',
                }}
                className="feature-card-line"
              />
            </article>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .card-slot {
          opacity: 0;
          transform: translateY(36px);
        }
        .card-slot.card-slot-visible {
          animation: card-rise 0.65s cubic-bezier(0.22,1,0.36,1) var(--delay,0s) both;
        }
        @keyframes card-rise {
          from { opacity: 0; transform: translateY(36px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .features-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }
        @media (min-width: 640px) {
          .features-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .features-grid { grid-template-columns: repeat(3, 1fr); }
        }
        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.06);
          border-color: var(--stroke-subtle);
        }
        .feature-card:hover .feature-card-line {
          opacity: 1 !important;
        }
      `}</style>
    </section>
  );
}

// ─── Section 3: Screenshots ───────────────────────────────────────────────────

function ScreenshotsSection() {
  const { ref: revealRef, visible } = useScrollReveal(0.1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(2); // center by default

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const max = scrollWidth - clientWidth;
      if (max <= 0) return;
      const idx = Math.round((scrollLeft / max) * (screenshots.length - 1));
      setActiveIdx(Math.min(Math.max(idx, 0), screenshots.length - 1));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToIndex = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const target = (i / (screenshots.length - 1)) * (el.scrollWidth - el.clientWidth);
    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  return (
    <section
      id="screenshots"
      aria-labelledby="gbt-screenshots-title"
      style={{
        padding: 'clamp(4rem, 10vw, 7rem) 0',
        background: 'linear-gradient(170deg, #0d1117 0%, #111827 50%, #0d1520 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background glow spots */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '20%',
          left: '15%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(74,127,183,0.14) 0%, transparent 70%)',
          pointerEvents: 'none',
          animation: 'glow-drift 20s ease-in-out infinite alternate',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(255,200,67,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
          animation: 'glow-drift 16s ease-in-out infinite alternate-reverse',
        }}
      />

      {/* Section header */}
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          marginBottom: 'clamp(2rem, 5vw, 3.5rem)',
        }}
      >
        <p
          style={{
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(74,127,183,0.85)',
            marginBottom: '0.75rem',
          }}
        >
          App Preview
        </p>
        <h2
          id="gbt-screenshots-title"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
            letterSpacing: '0.02em',
            margin: 0,
            lineHeight: 1.0,
            color: 'transparent',
            backgroundImage:
              'linear-gradient(135deg, #FFFFFF 30%, rgba(255,255,255,0.55) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          앱 미리보기
        </h2>
      </div>

      {/* Scroll area wrapper — constrains width per breakpoint, holds fade edges */}
      <div className="screenshots-scroll-area">
        {/* Anchor for IntersectionObserver */}
        <div ref={revealRef} style={{ position: 'absolute', width: 1, height: 1, pointerEvents: 'none' }} aria-hidden />

        {/* Fade edges — clipped by wrapper's overflow:hidden */}
        <div aria-hidden className="screenshots-fade screenshots-fade-l" />
        <div aria-hidden className="screenshots-fade screenshots-fade-r" />

        {/* Horizontal scroll track — TODO: Replace CSS mockups with actual screenshots */}
        <div
          ref={scrollRef}
          className="screenshots-track"
          style={{ display: 'flex', paddingBottom: '2rem', paddingTop: '1.5rem' }}
        >
        {screenshots.map((s, i) => {
          const isCenter = i === activeIdx;
          // Bloom from initial center (index 2) for entrance animation
          const delay = Math.abs(i - 2) * 0.13;
          return (
            <div
              key={s.label}
              className={`phone-slot${visible ? ' phone-slot-visible' : ''}`}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1.25rem',
                scrollSnapAlign: 'center',
                flexShrink: 0,
                position: 'relative',
                cursor: 'pointer',
                '--delay': `${delay}s`,
              } as React.CSSProperties}
            >
              {/* Spotlight halo */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: '10%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isCenter ? 220 : 140,
                  height: isCenter ? 220 : 140,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${
                    isCenter ? 'rgba(74,127,183,0.25)' : 'rgba(74,127,183,0.07)'
                  } 0%, transparent 70%)`,
                  filter: 'blur(20px)',
                  pointerEvents: 'none',
                  transition: 'width 0.4s ease, height 0.4s ease',
                }}
              />

              <PhoneMockup
                gradient={s.gradient}
                icon={s.icon}
                screenItems={s.screenItems}
                src={s.src}
                alt={`GirlsBandTabi ${s.label} 화면`}
                size="md"
                style={{
                  position: 'relative',
                  zIndex: 1,
                  transform: isCenter ? 'scale(1.1) translateY(-8px)' : 'scale(0.93)',
                  transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1)',
                }}
              />

              {/* Label pill */}
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  padding: '0.3rem 0.9rem',
                  borderRadius: '999px',
                  background: isCenter ? 'rgba(74,127,183,0.25)' : 'rgba(255,255,255,0.07)',
                  border: '1px solid',
                  borderColor: isCenter ? 'rgba(74,127,183,0.4)' : 'rgba(255,255,255,0.1)',
                  color: isCenter ? 'rgba(150,190,240,0.95)' : 'rgba(255,255,255,0.35)',
                  position: 'relative',
                  zIndex: 1,
                  transition: 'background 0.3s ease, color 0.3s ease, border-color 0.3s ease',
                }}
              >
                {s.label}
              </span>
            </div>
          );
        })}
        </div>{/* /screenshots-track */}
      </div>{/* /screenshots-scroll-area */}

      {/* Scroll progress dots — hidden on desktop via CSS */}
      <div className="screenshots-dots">
        {screenshots.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToIndex(i)}
            aria-label={`${screenshots[i].label} 화면으로 이동`}
            style={{
              width: i === activeIdx ? 22 : 6,
              height: 6,
              borderRadius: 3,
              background: i === activeIdx ? '#4A7FB7' : 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'width 0.3s ease, background 0.3s ease',
            }}
          />
        ))}
      </div>

      <style>{`
        /* ── entrance animation ── */
        .phone-slot {
          opacity: 0;
          transform: translateY(70px) scale(0.92);
        }
        .phone-slot.phone-slot-visible {
          animation: phone-rise 0.8s cubic-bezier(0.22,1,0.36,1) var(--delay,0s) both;
        }
        @keyframes phone-rise {
          from { opacity: 0; transform: translateY(70px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* ── scroll area wrapper ── */
        .screenshots-scroll-area {
          position: relative;
          overflow: hidden;
        }

        /* ── fade edges (clipped by wrapper) ── */
        .screenshots-fade {
          position: absolute;
          top: 0; bottom: 0;
          width: 3.5rem;
          pointer-events: none;
          z-index: 2;
        }
        .screenshots-fade-l {
          left: 0;
          background: linear-gradient(to right, #0d1117 20%, transparent);
        }
        .screenshots-fade-r {
          right: 0;
          background: linear-gradient(to left, #0d1117 20%, transparent);
        }

        /* ── scroll track ── */
        .screenshots-track {
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          gap: 1.75rem;
          padding-left: clamp(1rem, 5vw, 1.5rem);
          padding-right: clamp(1rem, 5vw, 1.5rem);
        }
        .screenshots-track::-webkit-scrollbar { display: none; }

        /* ── dots ── */
        .screenshots-dots {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          padding: 0 0 2.5rem;
        }

        /* ── tablet (640–1099px): constrain to ~3 phones visible ── */
        @media (min-width: 640px) and (max-width: 1099px) {
          .screenshots-scroll-area {
            max-width: 580px;
            margin: 0 auto;
          }
        }

        /* ── desktop (≥1100px): show all 5, centered, no scroll ── */
        @media (min-width: 1100px) {
          .screenshots-scroll-area {
            overflow: visible;
            max-width: 1100px;
            margin: 0 auto;
          }
          .screenshots-track {
            overflow-x: visible;
            scroll-snap-type: none;
            justify-content: center;
            gap: 2.5rem;
            padding-left: 2rem;
            padding-right: 2rem;
          }
          .screenshots-fade { display: none; }
          .screenshots-dots { display: none; }
        }
      `}</style>
    </section>
  );
}

// ─── Section 4: How It Works ──────────────────────────────────────────────────

function HowItWorksSection() {
  const { ref: stepsRef, visible } = useScrollReveal(0.12);
  return (
    <section
      aria-labelledby="gbt-how-title"
      style={{
        padding: 'clamp(4rem, 10vw, 7rem) clamp(1.25rem, 5vw, 2.5rem)',
        background: 'var(--bg)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Section header */}
        <div style={{ marginBottom: 'clamp(2.5rem, 6vw, 4.5rem)' }}>
          <p
            style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--accent-yellow)',
              marginBottom: '0.75rem',
            }}
          >
            3 Simple Steps
          </p>
          <h2
            id="gbt-how-title"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              letterSpacing: '0.02em',
              margin: 0,
              lineHeight: 1.0,
              color: 'transparent',
              backgroundImage:
                'linear-gradient(135deg, var(--text) 40%, var(--accent-yellow) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            HOW IT WORKS
          </h2>
        </div>

        {/* Steps */}
        <div className="steps-wrapper" ref={stepsRef}>
          {/* Connector line — desktop only */}
          <div className="steps-connector" aria-hidden />

          <ol className="steps-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {steps.map((s, i) => {
              const accentColors = [
                { fill: '#4A7FB7', glow: 'rgba(74,127,183,0.3)' },
                { fill: '#FFC843', glow: 'rgba(255,200,67,0.3)' },
                { fill: '#77DD77', glow: 'rgba(119,221,119,0.3)' },
              ];
              const ac = accentColors[i];
              return (
                <li
                  key={s.num}
                  className={`step-item step-slot${visible ? ' step-slot-visible' : ''}`}
                  style={{ '--delay': `${i * 0.15}s` } as React.CSSProperties}
                >
                  {/* Number circle */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '1rem',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${ac.fill}22, ${ac.fill}08)`,
                        border: `2px solid ${ac.fill}55`,
                        boxShadow: `0 0 24px ${ac.glow}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{s.icon}</span>
                      <span
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: '0.65rem',
                          letterSpacing: '0.1em',
                          color: ac.fill,
                          lineHeight: 1,
                        }}
                      >
                        {s.num}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="step-content">
                    <h3
                      style={{
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        margin: '0 0 0.5rem',
                        color: 'var(--text)',
                      }}
                    >
                      {s.title}
                    </h3>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        color: 'var(--muted)',
                        margin: 0,
                        lineHeight: 1.8,
                      }}
                    >
                      {s.desc}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <style>{`
        .step-slot {
          opacity: 0;
          transform: translateY(32px);
        }
        .step-slot.step-slot-visible {
          animation: step-rise 0.65s cubic-bezier(0.22,1,0.36,1) var(--delay,0s) both;
        }
        @keyframes step-rise {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 599px) {
          .step-item { gap: 1rem; }
          .step-content { padding-top: 0.25rem; }
        }
        .steps-wrapper {
          position: relative;
        }
        .steps-connector {
          display: none;
        }
        .steps-list {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
          position: relative;
          z-index: 1;
        }
        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
        }
        .step-content {
          padding-top: 0.75rem;
          flex: 1;
        }
        @media (min-width: 768px) {
          .steps-connector {
            display: block;
            position: absolute;
            top: 36px;
            left: calc(16.666% - 1px);
            right: calc(16.666% - 1px);
            height: 1px;
            background: linear-gradient(90deg,
              rgba(74,127,183,0.5) 0%,
              rgba(255,200,67,0.5) 50%,
              rgba(119,221,119,0.5) 100%
            );
            z-index: 0;
          }
          .steps-list {
            flex-direction: row;
            align-items: flex-start;
            gap: 0;
          }
          .step-item {
            flex: 1;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 0 2rem;
            gap: 1.25rem;
          }
          .step-content {
            padding-top: 0;
          }
        }
      `}</style>
    </section>
  );
}

// ─── Section 5: Download CTA ──────────────────────────────────────────────────

function DownloadCTASection() {
  return (
    <section
      id="download"
      aria-labelledby="gbt-cta-title"
      className="grain-overlay"
      style={{
        padding: 'clamp(5rem, 14vw, 8rem) clamp(1.25rem, 5vw, 2.5rem)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #0a0f1e 0%, #0d1530 40%, #141022 100%)',
      }}
    >
      {/* Glow center burst */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '800px',
          height: '800px',
          background:
            'radial-gradient(ellipse at center, rgba(74,127,183,0.18) 0%, rgba(255,200,67,0.06) 40%, transparent 70%)',
          pointerEvents: 'none',
          animation: 'glow-drift 24s ease-in-out infinite alternate',
        }}
      />

      {/* Diagonal stripe accents */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(74,127,183,0.6) 30%, rgba(255,200,67,0.6) 70%, transparent 100%)',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: '640px', margin: '0 auto' }}>
        {/* Eyebrow */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1.75rem',
            padding: '0.4rem 1rem',
            borderRadius: '999px',
            border: '1px solid rgba(255,200,67,0.25)',
            background: 'rgba(255,200,67,0.06)',
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: '#FFC843',
              display: 'inline-block',
              boxShadow: '0 0 8px rgba(255,200,67,0.8)',
            }}
          />
          <span
            style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(255,200,67,0.85)',
            }}
          >
            2026년 상반기 출시 예정
          </span>
        </div>

        <h2
          id="gbt-cta-title"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2.5rem, 7vw, 5rem)',
            letterSpacing: '0.02em',
            margin: '0 0 1.25rem',
            lineHeight: 1.0,
            color: 'transparent',
            backgroundImage:
              'linear-gradient(135deg, #FFFFFF 0%, #C7D9F0 40%, #4A7FB7 65%, #FFC843 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'string-shimmer 5s linear infinite',
          }}
        >
          지금 바로<br />시작하세요
        </h2>

        <p
          style={{
            fontSize: '0.95rem',
            color: 'rgba(255,255,255,0.45)',
            marginBottom: '2.75rem',
            lineHeight: 1.8,
          }}
        >
          앱스토어 출시를 기다려주세요 —<br />
          여러분의 순례 여정이 시작될 준비를 하고 있습니다.
        </p>

        {/* Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: '3rem',
          }}
        >
          <StoreButton store="App Store" variant="dark" />
          <StoreButton store="Google Play" variant="dark" />
        </div>

        {/* Platform badges */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap',
          }}
        >
          {['iOS 16+', 'Android 10+', 'Flutter'].map((badge) => (
            <span
              key={badge}
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: 'rgba(255,255,255,0.3)',
              }}
            >
              {badge}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function GirlsBandTabiPage() {
  return (
    <main>
      <HeroSection />
      <FeaturesSection />
      <ScreenshotsSection />
      <HowItWorksSection />
      <DownloadCTASection />
    </main>
  );
}
